import { ApiRequestError } from '@/lib/api';

/**
 * Users see sentences, never status codes or exception text. The server's own
 * message is trusted only for the domain errors it writes deliberately —
 * rule violations and validation, which already read as plain language.
 */
const CODE_MESSAGES: Record<string, string> = {
  unauthorized: 'Your session has ended. Sign in again to continue.',
  unauthenticated: 'Your session has ended. Sign in again to continue.',
  forbidden: 'You do not have access to this.',
  not_found: 'That is no longer available. It may have been moved or archived.',
  conflict: 'Someone else changed this while you were working. Reload and try again.',
  rate_limited: 'Too many attempts. Wait a few minutes before trying again.',
  payload_too_large: 'That file is too large to upload.',
  unsupported_media_type: 'That file type is not supported.',
  internal_error: 'Something went wrong on our end. Try again in a moment.',
  network_error: 'Cannot reach the server. Check your connection and try again.',
  bad_response: 'The server sent something unexpected. Try again in a moment.',
};

/** Codes whose server-authored message is written for users already. */
const TRUSTED_CODES = new Set([
  'validation_error',
  'rule_violation',
  'conflict',
  'password.change_required',
  'invalid_state_transition',
  'period_closed',
  'gstin.invalid',
  'gstin.not_found',
  'gstin.lookup_failed',
  'gstin.lookup_credits_exhausted',
  'gstin.lookup_unavailable',
]);

const FALLBACK = 'Something went wrong. Try again.';

export function errorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (TRUSTED_CODES.has(err.code) && err.message.trim().length > 0) {
      return err.message;
    }
    const mapped = CODE_MESSAGES[err.code];
    if (mapped) return mapped;
    if (err.status >= 500) return CODE_MESSAGES.internal_error ?? FALLBACK;
    // A 4xx we have no phrasing for: the server message is still better than
    // a status code, but only when it does not read like a raw HTTP failure.
    if (err.message.trim().length > 0 && !/^Request failed/i.test(err.message)) {
      return err.message;
    }
    return FALLBACK;
  }
  return FALLBACK;
}
